import {
  Button,
  Flex,
  Grid,
  Heading,
  Typography,
  WinCodeCard,
} from "@sidelinesports/fan-design-system";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  type ApiEndMessage,
  type ApiGameDetails,
  type ApiUploadedImage,
  getEndMessage,
} from "../../../../api";
import type {
  DialogContentProps,
  DialogRootProps,
} from "../../../../components";
import { Dialog, Markdown, UploadedImage } from "../../../../components";
import styles from "./PostGameMessage.module.css";
import { PostGamePositionTitle } from "./PostGamePositionTitle";

export type PostGameMessageProps = {
  game: ApiGameDetails;
  position: number;
};

export const PostGameMessage = ({
  game,
  position,
  onFinishClose,
  trigger,
  ...dialogRootProps
}: PostGameMessageProps & { trigger?: React.ReactNode } & Omit<
    DialogRootProps,
    "children"
  > &
  Pick<DialogContentProps, "onFinishClose">) => {
  const endMessageQuery = useQuery({
    queryKey: ["games", game.id, "end-message"],
    queryFn: async () => {
      return getEndMessage(game.id);
    },
  });

  if (endMessageQuery.isPending) {
    return null;
  }

  if (endMessageQuery.isError) {
    return null;
  }

  if (!endMessageQuery.data) {
    return null;
  }

  const endMessage = endMessageQuery.data;

  const messageHasHeroImage = hasHeroImage(endMessage);
  const messageHasStandardImage = hasStandardImage(endMessage);

  return (
    <Dialog.Root {...dialogRootProps}>
      {trigger}
      <Dialog.Content
        size={messageHasHeroImage ? "fullscreen" : "lg"}
        gap="lg"
        bg="white"
        onFinishClose={onFinishClose}
        name="PostGameMessageDialog"
      >
        <PostGameMessageDialogHeader
          game={game}
          heroImage={messageHasHeroImage ? endMessage.image : undefined}
        />
        <Dialog.Illustration>
          {messageHasStandardImage ? (
            <UploadedImage
              image={endMessage.image}
              alt=""
              objectFit="contain"
              maxWidth="12.5rem"
              maxHeight="12.5rem"
              m="auto"
              tracking={{
                enabled: true,
                location: "PostGameMessage::EndMessageImage",
              }}
            />
          ) : messageHasHeroImage ? null : (
            <PostGamePositionTitle position={position} />
          )}
        </Dialog.Illustration>
        {endMessage.codeInfo?.code && (
          <WinCodeCard
            className={styles["win-code-box"]}
            code={endMessage.codeInfo.code}
            format={endMessage.codeInfo.codeType}
            barcodeFormat={endMessage.codeInfo.barcodeType || undefined}
          />
        )}
        <Dialog.Body>
          <Grid gap={16}>
            <Heading level={2}>{endMessage.title}</Heading>
            <Markdown>{endMessage.text}</Markdown>
          </Grid>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Close as={Button} size="sm">
            Close
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const PostGameMessageDialogHeader = ({
  game,
  heroImage,
}: {
  game: ApiGameDetails;
  heroImage: ApiUploadedImage | undefined;
}) => {
  const gameDate = useMemo(() => {
    return new Date(game.timestamp).toLocaleDateString();
  }, [game.timestamp]);

  return (
    <div
      className={
        heroImage
          ? styles["message-header-with-hero"]
          : styles["message-header-no-hero"]
      }
      style={{
        backgroundImage: heroImage ? `url(${heroImage.url})` : undefined,
      }}
    >
      <Flex
        className={styles["flex-container"]}
        justify="end"
        align="center"
        gap={16}
      >
        <Typography textAlign="right" size="md">
          {gameDate}
        </Typography>
        <Dialog.Close />
      </Flex>
    </div>
  );
};

function hasImage(message: ApiEndMessage): message is ApiEndMessage & {
  image: ApiUploadedImage;
} {
  return Boolean(message.image);
}

function hasHeroImage(message: ApiEndMessage): message is ApiEndMessage & {
  image: ApiUploadedImage;
} {
  return hasImage(message) && message.imageDisplayFormat === "cover";
}

function hasStandardImage(message: ApiEndMessage): message is ApiEndMessage & {
  image: ApiUploadedImage;
} {
  return hasImage(message) && message.imageDisplayFormat === "contain";
}
